<template>
  <div class="product-update">
    <div
      class="product-update-btn"
      :class="{ active: isActive }"
      @click="handleClick"
      @mouseenter="isHover = true"
      @mouseleave="isHover = false"
    >
      <div class="update-icon-wrapper">
        <img
          :src="currentIcon"
          alt="产品更新"
          class="update-icon"
        />
        <span
          v-if="hasUpdate"
          class="update-dot"
          @click.stop="handleDotClick"
        />
      </div>
      <span class="update-text">产品更新</span>
    </div>
    <iframe
      ref="productIframe"
      src="https://testdocumentation.aifxy.com/document"
      class="product-iframe"
      frameborder="0"
      @load="onIframeLoad"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'Product',
  data() {
    return {
      isHover: false,
      hasUpdate: false,
      updateResult: null,
      latestDocId: null,
      normalIcon: require('@/assets/image/product/doc/icn_update.png'),
      hoverIcon: require('@/assets/image/product/doc/icn_update_hover.png')
    }
  },
  computed: {
    ...mapGetters(['userInfo']),
    isActive() {
      return this.$route?.path?.startsWith('/productDoc')
    },
    currentIcon() {
      return this.isHover || this.isActive ? this.hoverIcon : this.normalIcon
    },
    userPhone() {
      return this.userInfo?.phoneNumber || ''
    }
  },
  mounted() {
    // 监听来自 iframe 的 postMessage 消息
    console.log('[mounted] 注册 postMessage 事件监听器')
    window.addEventListener('message', this.handleIframeMessage)
    // 也监听所有消息，用于调试
    window.addEventListener('message', (event) => {
      console.log('[mounted] 全局消息监听器 - 收到消息:', {
        origin: event.origin,
        data: event.data,
        source: event.source
      })
    })
  },
  beforeDestroy() {
    // 移除事件监听器
    window.removeEventListener('message', this.handleIframeMessage)
  },
  methods: {
    handleClick(result = null) {
      // 点击后清除更新提示
      this.hasUpdate = false

      // 如果 result 不是版本数字，则将其设置为 null
      if (result !== null && result !== undefined && !this.isVersionNumber(result)) {
        result = null
      }

      const updateData = result || this.updateResult

      // 如果 result 有值，调用 iframe 中的 updatePotBrand 方法
      if (updateData) {
        this.callUpdatePotBrand(updateData)
      }

      if (!this.isActive) {
        // 如果有更新数据，通过 query 参数传递
        if (result) {
          const query = updateData ? { updateData: JSON.stringify(updateData) } : ''
          this.$router.push({
            path: '/productdoc',
            query
          })
        } else {
          this.$router.push({
            path: '/productdoc'
          })
        }
      }
      // 清除保存的结果
      this.updateResult = null
    },
    handleDotClick() {
      // 点击红点时，传递保存的结果值
      this.handleClick(this.updateResult)
    },
    onIframeLoad() {
      // iframe 加载完成后，延迟一下确保完全加载
      console.log('[onIframeLoad] iframe 加载完成，准备调用 checkUpdate')
      this.$nextTick(() => {
        setTimeout(() => {
          console.log('[onIframeLoad] 延迟后调用 checkUpdate')
          this.callCheckUpdate()
        }, 500) // 增加延迟时间，确保 iframe 完全加载并准备好接收消息
      })
    },
    callCheckUpdate() {
      const iframe = this.$refs.productIframe
      if (!iframe) {
        return
      }

      const iframeWindow = iframe.contentWindow
      if (!iframeWindow) {
        return
      }

      // 检查 iframe 是否已加载到正确的 URL
      try {
        const iframeLocation = iframe.contentWindow.location.href
        if (!iframeLocation.includes('https://testdocumentation.aifxy.com')) {
          return
        }
      } catch (e) {
        // 跨域情况下无法读取 location，这是正常的
      }

      // 先尝试直接调用（同域情况下）
      try {
        if (typeof iframeWindow.checkUpdate === 'function') {
          console.log('[checkUpdate] 直接调用 - 输入参数:', { phone: this.userPhone })
          const result = iframeWindow.checkUpdate(this.userPhone)
          // 处理返回值：可能是同步返回值或 Promise
          if (result && typeof result.then === 'function') {
            // 如果是 Promise，等待结果
            result
              .then(res => {
                console.log('[checkUpdate] 直接调用 - 返回值 (Promise):', res)
                this.updateHasUpdate(res)
              })
              .catch(err => {
                console.error('[checkUpdate] 直接调用 - 错误:', err)
              })
          } else {
            // 如果是同步返回值，直接处理
            console.log('[checkUpdate] 直接调用 - 返回值 (同步):', result)
            this.updateHasUpdate(result)
          }
          return
        }
      } catch (error) {
        // 跨域情况下会抛出 SecurityError，使用 postMessage
        console.log('[checkUpdate] 直接调用失败，使用 postMessage:', error)
      }

      // 使用 postMessage 方式（跨域场景）
      const messageData = {
        type: 'checkUpdate',
        phone: this.userPhone
      }
      const targetOrigin = 'https://testdocumentation.aifxy.com'
      console.log('[checkUpdate] postMessage 发送 - 输入参数:', messageData)
      console.log('[checkUpdate] postMessage 发送 - targetOrigin:', targetOrigin)

      try {
        // 先尝试使用具体的 origin
        iframeWindow.postMessage(
          messageData,
          targetOrigin
        )
        console.log('[checkUpdate] postMessage 发送成功 (使用具体 origin)')
      } catch (error) {
        console.warn('[checkUpdate] postMessage 发送失败 (具体 origin)，尝试使用通配符:', error)
        // 如果指定 origin 失败，尝试使用 '*'（不推荐，但可以测试）
        try {
          iframeWindow.postMessage(messageData, '*')
          console.log('[checkUpdate] postMessage 发送成功 (使用通配符 *)')
        } catch (e2) {
          console.error('[checkUpdate] postMessage 发送失败 (所有方式都失败):', e2)
        }
      }
    },
    handleIframeMessage(event) {
      // 打印所有收到的消息（用于调试）- 先打印所有消息，不管 origin
      const expectedOrigin = 'https://testdocumentation.aifxy.com'
      console.log('[postMessage] 收到所有消息 (包括其他来源):', {
        origin: event.origin,
        data: event.data,
        dataType: typeof event.data,
        expectedOrigin: expectedOrigin
      })

      // 验证消息来源（origin 应该是协议+域名，不包含路径和末尾斜杠）
      // 兼容处理：去掉末尾斜杠进行比较
      const normalizedOrigin = event.origin.replace(/\/$/, '')
      const normalizedExpected = expectedOrigin.replace(/\/$/, '')

      if (normalizedOrigin !== normalizedExpected) {
        console.log('[postMessage] 消息来源不匹配，已忽略。来源:', event.origin, '期望:', expectedOrigin)
        // 即使不匹配也打印出来，方便调试
        return
      }

      console.log('[postMessage] 消息来源验证通过，开始处理消息')

      // 处理 updatePotBrand 的返回值
      if (event.data && event.data.type === 'updatePotBrandResult') {
        const result = event.data.result
        console.log('[updatePotBrand] postMessage 接收 - 返回值:', result)
        // 如果更新成功，隐藏红点
        if (result === true || result === 'success' || (result && result.success)) {
          this.updateHasUpdate(false)
        }
        return
      }

      // 处理 checkUpdate 的返回值
      if (event.data && event.data.type === 'checkUpdateResult') {
        const result = event.data.result
        console.log('[checkUpdate] postMessage 接收 - 返回值 (checkUpdateResult):', result)
        this.updateHasUpdate(result)
        return
      }

      // 也处理其他可能的返回格式
      if (event.data && typeof event.data === 'object') {
        // 处理 updatePotBrand 的其他返回格式
        if (event.data.type === 'updatePotBrand' && 'result' in event.data) {
          const result = event.data.result
          console.log('[updatePotBrand] postMessage 接收 - 返回值 (updatePotBrand格式):', result)
          // 如果更新成功，隐藏红点
          if (result === true || result === 'success' || (result && result.success)) {
            this.updateHasUpdate(false)
          }
          return
        }
        // 如果直接返回了结果（可能是 checkUpdate 的结果）
        if ('result' in event.data && !event.data.type) {
          const result = event.data.result
          console.log('[checkUpdate] postMessage 接收 - 返回值 (直接格式，无type):', result)
          this.updateHasUpdate(result)
          return
        }
        // 如果返回的是 checkUpdate 方法的结果（可能是直接调用返回的）
        if (event.data.type === 'checkUpdate' && 'result' in event.data) {
          const result = event.data.result
          console.log('[checkUpdate] postMessage 接收 - 返回值 (checkUpdate格式):', result)
          this.updateHasUpdate(result)
          return
        }
        // 处理可能直接返回数据的情况（没有包装在 result 中）
        if (event.data.type === 'checkUpdate' && 'data' in event.data) {
          const result = event.data.data
          console.log('[checkUpdate] postMessage 接收 - 返回值 (checkUpdate data格式):', result)
          this.updateHasUpdate(result)
          return
        }
      }

      // 处理非对象格式的返回值（可能是直接返回的字符串或数字）
      if (event.data !== null && event.data !== undefined && typeof event.data !== 'object') {
        console.log('[checkUpdate] postMessage 接收 - 返回值 (非对象格式):', event.data)
        // 如果返回的是版本号字符串或数字，也处理
        if (this.isVersionNumber(event.data)) {
          this.updateHasUpdate(event.data)
          return
        }
      }

      // 如果以上都没有匹配，打印未处理的消息以便调试
      console.warn('[postMessage] 收到未处理的消息，格式:', {
        data: event.data,
        dataType: typeof event.data,
        isArray: Array.isArray(event.data),
        keys: event.data && typeof event.data === 'object' ? Object.keys(event.data) : 'N/A'
      })
    },
    updateHasUpdate(result) {
      console.log('[updateHasUpdate] 调用 - 输入参数:', {
        result: result,
        resultType: typeof result,
        isNull: result === null,
        isUndefined: result === undefined,
        isFalse: result === false
      })
      // 如果返回值不为 false，则显示红点提示并保存结果值
      if (result !== false && result != null) {
        this.hasUpdate = true
        this.updateResult = result
        console.log('[updateHasUpdate] 设置红点显示 - hasUpdate:', this.hasUpdate, 'updateResult:', this.updateResult)
      } else {
        this.hasUpdate = false
        this.updateResult = null
        console.log('[updateHasUpdate] 隐藏红点 - hasUpdate:', this.hasUpdate)
      }
    },
    isVersionNumber(value) {
      // 检查是否是版本数字格式
      // 支持格式：纯数字（如 "1", "123"）或点分隔的版本号（如 "1.0", "1.0.0", "2.1.3"）
      if (value === null || value === undefined) {
        return false
      }

      // 转换为字符串
      const str = String(value).trim()

      // 空字符串不是版本号
      if (str === '') {
        return false
      }

      // 检查是否是纯数字
      if (/^\d+$/.test(str)) {
        return true
      }

      // 检查是否是点分隔的版本号格式（如 1.0, 1.0.0, 2.1.3）
      // 版本号格式：数字.数字.数字...（至少一个点，每部分都是数字）
      if (/^\d+(\.\d+)+$/.test(str)) {
        return true
      }

      return false
    },
    callUpdatePotBrand(result) {
      this.latestDocId = result
      const iframe = this.$refs.productIframe
      if (!iframe) {
        return
      }

      const iframeWindow = iframe.contentWindow
      if (!iframeWindow) {
        return
      }

      // 先尝试直接调用（同域情况下）
      try {
        // console.log('[updatePotBrand] 直接调用 - 输入参数:', { phone: this.userPhone, result: this.latestDocId })
        if (typeof iframeWindow.updatePotBrand === 'function') {
          const updateResult = iframeWindow.updatePotBrand(this.userPhone, this.latestDocId)
          // 处理返回值：可能是同步返回值或 Promise
          if (updateResult && typeof updateResult.then === 'function') {
            // 如果是 Promise，等待结果
            updateResult
              .then(res => {
                console.log('[updatePotBrand] 直接调用 - 返回值 (Promise):', res)
                if (res === true || res === 'success' || (res && res.success)) {
                  // 更新成功，隐藏红点
                  this.updateHasUpdate(false)
                }
              })
              .catch(err => {
                console.error('[updatePotBrand] 直接调用 - 错误:', err)
              })
          } else {
            // 如果是同步返回值，直接处理
            console.log('[updatePotBrand] 直接调用 - 返回值 (同步):', updateResult)
            if (updateResult === true || updateResult === 'success' || (updateResult && updateResult.success)) {
              // 更新成功，隐藏红点
              this.updateHasUpdate(false)
            }
          }
          console.log('[updatePotBrand] 直接调用成功')
          return
        }
      } catch (error) {
        // 跨域情况下会抛出 SecurityError，使用 postMessage
        console.log('[updatePotBrand] 直接调用失败，使用 postMessage:', error)
      }

      // 使用 postMessage 方式（跨域场景）
      const targetOrigin = 'https://testdocumentation.aifxy.com'
      try {
        const messageData = {
          type: 'updatePotBrand',
          phone: this.userPhone,
          latestDocId: this.latestDocId
        }
        console.log('[updatePotBrand] postMessage 发送 - 输入参数:', messageData)
        iframeWindow.postMessage(
          messageData,
          targetOrigin
        )
        console.log('[updatePotBrand] postMessage 发送成功')
      } catch (error) {
        // 如果指定 origin 失败，尝试使用 '*'（不推荐，但可以测试）
        try {
          const messageData = {
            type: 'updatePotBrand',
            phone: this.userPhone,
            latestDocId: this.latestDocId
          }
          console.log('[updatePotBrand] postMessage 发送 (使用通配符) - 输入参数:', messageData)
          iframeWindow.postMessage(messageData, '*')
          console.log('[updatePotBrand] postMessage 发送成功 (使用通配符)')
        } catch (e2) {
          console.error('[updatePotBrand] postMessage 发送失败:', e2)
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.product-update {
  flex-shrink: 0;
  height: auto;
  margin: 0 16px;
  display: inline-flex;
  align-items: center;

  .product-update-btn {
    display: inline-flex;
    align-items: center;
    padding: 6px 12px;
    border-radius: 6px;
    border: 1px solid #dcdfe5;
    background: #fff;
    cursor: pointer;
    transition: all 0.2s ease;
    box-sizing: border-box;
    color: #303133; // 默认与灰色图标保持一致

    &:hover,
    &.active {
      border-color: #0c6fff;
      color: #0c6fff;
    }

    .update-icon-wrapper {
      position: relative;
      margin-right: 6px;
      display: flex;
      align-items: center;
      flex-shrink: 0;
    }

    .update-icon {
      width: 16px;
      height: 16px;
      display: block;
    }

    .update-dot {
      position: absolute;
      top: -12px;
      right: -80px;
      width: 12px;
      height: 12px;
      background: #f56c6c;
      border-radius: 50%;
      border: 1px solid #fff;
      display: block;
      cursor: pointer;
      z-index: 10;
    }

    .update-text {
      font-family: 'PingFang SC', 'PingFang SC';
      font-weight: 500;
      font-size: 14px;
      line-height: 1;
      white-space: nowrap;
      transition: color 0.2s ease;
    }
  }

  .product-iframe {
    position: absolute;
    left: -9999px;
    top: -9999px;
    width: 0;
    height: 0;
    opacity: 0;
    pointer-events: none;
    visibility: hidden;
  }
}
</style>

